import React from 'react';

import { useModalPresenceStore } from 'store/modal-presence';

import { createChainedFunction, getHasTransition } from '../../utils/helpers';

import Backdrop from '../Backdrop';
import '../Dialog';
import { OnCloseFn } from '../Dialog/Dialog';
import Portal from '../Portal';
import useOverlayStore from '../../store/overlay';
import FocusTrap from './FocusTrap';

type Props = {
  children: any;
  isOpen: boolean;
  hideBackdrop?: boolean;
  onClick?: OnCloseFn;
  onClose: (...args: any) => void;
  // TODO: Needs to establish API args for the function
  backdropProps: Record<string, any>;
  role?: string;
  className?: string;
  ref?: any;
};

const Modal = (props: Props) => {
  const { children, isOpen, hideBackdrop = false, onClose, backdropProps, ...rest } = props;
  const hasTransition = getHasTransition(props);
  const [exited, setExited] = React.useState(!isOpen);
  // Single source of truth for "is this box on screen": it drives both the presence count and the
  // bail-out below, so the two can never disagree.
  const isVisible = isOpen || (hasTransition && !exited);

  const handleExited = React.useCallback(() => {
    setExited(true);
  }, [setExited]);

  const handleEnter = React.useCallback(() => {
    setExited(false);
  }, [setExited]);

  React.useLayoutEffect(() => {
    if (!exited) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [exited]);

  // Overlay counter, so fixed-position chrome steps out from under this modal.
  // Keyed off main's `isVisible`, which is the same expression our `isMounted` was.
  const openOverlay = useOverlayStore((state) => state.openOverlay);
  const closeOverlay = useOverlayStore((state) => state.closeOverlay);

  React.useEffect(() => {
    if (!isVisible) return undefined;
    openOverlay();
    return closeOverlay;
  }, [isVisible, openOverlay, closeOverlay]);

  // Body/cleanup only, so it stays balanced under StrictMode's double invoke and when a route
  // change unmounts the modal while it is still open.
  React.useEffect(() => {
    if (!isVisible) return undefined;
    const { push, pop } = useModalPresenceStore.getState();
    push();
    return pop;
  }, [isVisible]);

  const handleBackdropClick = React.useCallback(
    (ev: React.SyntheticEvent<HTMLElement>) => {
      if (ev.target !== ev.currentTarget) {
        return;
      }

      if (onClose) {
        onClose(ev, 'backdropClick');
      }
    },
    [onClose],
  );

  const handleKeyDown = (ev: React.SyntheticEvent<HTMLElement>) => {
    // @ts-expect-error
    if (ev.key === 'Escape') {
      ev.stopPropagation();
    }
  };

  if (!isVisible) {
    return null;
  }

  const childProps = {};

  if (hasTransition) {
    // @ts-expect-error
    childProps.onEnter = createChainedFunction(
      handleEnter, // children.props.onEnter
    );
    // @ts-expect-error
    childProps.onExited = createChainedFunction(handleExited, children.props.onExited);
  }

  return (
    <Portal>
      {/* @ts-expect-error */}
      <div
        style={{
          top: '0',
          left: '0',
          // Portalled to <body>, so the app shell's page inset cannot reach it. Every legacy
          // Dialog centres inside this box and every SidePanel pins to its right edge, so this
          // one line is what keeps both on the page instead of under the assistant drawer.
          right: 'var(--app-assistant-inset, 0px)',
          bottom: '0',
          zIndex: '1300',
          position: 'fixed',
          transition: 'right 0.25s ease-in-out',
        }}
        className="dialog"
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {hideBackdrop ? null : (
          <Backdrop onClick={handleBackdropClick} isOpen={isOpen} duration={175} {...backdropProps} />
        )}
        <FocusTrap>{React.cloneElement(children, childProps)}</FocusTrap>
      </div>
    </Portal>
  );
};

export default Modal;
